<template>
  <section ref="$root" class="filter-bar" :class="{ 'filter-bar--focused': isOpen }" @click="focusInput">
    <font-awesome-icon :icon="['fal', 'search']" class="filter-bar__icon" />

    <span v-if="search" class="filter-bar__token filter-bar__token--search">
      <span class="filter-bar__token-label">Search</span>
      <span class="filter-bar__token-value">{{ search }}</span>
      <button type="button" class="filter-bar__token-remove" aria-label="Clear search" @click.stop="clearSearch">
        <font-awesome-icon :icon="['fal', 'times']" />
      </button>
    </span>

    <span v-for="token in visibleTokens" :key="token.key" class="filter-bar__token">
      <button
        type="button"
        class="filter-bar__token-edit"
        :aria-label="`Edit filter ${getFilter(token.key)?.label}`"
        :title="`${getFilter(token.key)?.label} ${symbolFor(token)} ${displayValue(token)}`"
        @click.stop="editToken(token)"
      >
        <span class="filter-bar__token-label">{{ getFilter(token.key)?.label }}</span>
        <span class="filter-bar__token-op">{{ symbolFor(token) }}</span>
        <span class="filter-bar__token-value">{{ displayValue(token) }}</span>
      </button>
      <button
        v-if="!isPinned(token.key)"
        type="button"
        class="filter-bar__token-remove"
        :aria-label="`Remove filter ${getFilter(token.key)?.label}`"
        @click.stop="removeToken(token.key)"
      >
        <font-awesome-icon :icon="['fal', 'times']" />
      </button>
    </span>

    <span v-if="draftKey" class="filter-bar__draft">
      <span class="filter-bar__token-label">{{ draftFilter?.label }}</span>
      <span v-if="draftOp" class="filter-bar__token-op">{{ operatorSymbolFor(draftOp) }}</span>
    </span>

    <input
      ref="$input"
      v-model="query"
      class="filter-bar__input"
      :placeholder="tokens.length || draftKey || search ? '' : placeholder"
      aria-label="Filter list"
      role="combobox"
      aria-autocomplete="list"
      :aria-controls="menuId"
      :aria-expanded="String(isOpen)"
      :aria-activedescendant="highlight >= 0 ? optionId(highlight) : null"
      maxlength="200"
      autocomplete="off"
      @focus="isOpen = true"
      @input="isOpen = true"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter.prevent="onEnter"
      @keydown.tab.exact="onTab"
      @keydown.esc.prevent="cancelDraft"
      @keydown.backspace="onBackspace"
    />

    <button
      v-if="canClearAll"
      type="button"
      class="filter-bar__clear"
      aria-label="Clear all filters"
      @click.stop="clearAll"
    >
      <font-awesome-icon :icon="['fal', 'times']" />
    </button>

    <div
      v-if="isOpen"
      :id="menuId"
      ref="$menu"
      :role="menuRole"
      :aria-label="menuRole === 'group' ? 'Filter value' : null"
      class="filter-bar__menu"
      @click.stop
      @keydown.enter.stop.prevent="commitDraft"
      @keydown.esc.prevent="cancelDraft"
    >
      <template v-if="stage === 'column'">
        <button
          v-for="(col, i) in columnSuggestions"
          :id="optionId(i)"
          role="option"
          :aria-selected="String(i === highlight)"
          :key="col.key"
          type="button"
          class="filter-bar__option"
          :class="{ 'filter-bar__option--active': i === highlight }"
          @mouseenter="highlight = i"
          @mousedown.prevent
          @keydown.enter.stop.prevent="pickColumn(col)"
          @click.stop="pickColumn(col)"
        >
          {{ col.label }}
        </button>
        <div v-if="!columnSuggestions.length" class="filter-bar__empty">No matching filters</div>
        <button
          v-if="searchable && query.trim()"
          type="button"
          :id="optionId(columnSuggestions.length)"
          role="option"
          :aria-selected="String(highlight === columnSuggestions.length)"
          class="filter-bar__option"
          :class="{ 'filter-bar__option--active': highlight === columnSuggestions.length }"
          @mouseenter="highlight = columnSuggestions.length"
          @mousedown.prevent
          @keydown.enter.stop.prevent="commitSearch"
          @click.stop="commitSearch"
        >
          Search for “{{ query.trim() }}” <span class="filter-bar__option-hint">name or email</span>
        </button>
      </template>

      <template v-else-if="stage === 'operator'">
        <div v-if="!visibleOperators.length" class="filter-bar__empty">No matching operators</div>
        <button
          v-for="(op, i) in visibleOperators"
          :id="optionId(i)"
          role="option"
          :aria-selected="String(i === highlight)"
          :key="op"
          type="button"
          class="filter-bar__option"
          :class="{ 'filter-bar__option--active': i === highlight }"
          @mouseenter="highlight = i"
          @mousedown.prevent
          @keydown.enter.stop.prevent="pickOperator(op)"
          @click.stop="pickOperator(op)"
        >
          {{ OPERATOR_LABELS[op] || op }}
        </button>
      </template>

      <template v-else-if="stage === 'value'">
        <template v-if="optionTypeahead">
          <div v-if="!valueStageOptions.length" class="filter-bar__empty">No matching options</div>
          <button
            v-for="(opt, i) in valueStageOptions"
            :id="optionId(i)"
            :key="opt.value"
            type="button"
            role="option"
            :aria-selected="String(i === highlight)"
            :aria-checked="isOptionChecked(opt.value) ? 'true' : null"
            class="filter-bar__option"
            :class="{
              'filter-bar__option--active': i === highlight,
              'filter-bar__option--selected': isOptionChecked(opt.value),
            }"
            @mouseenter="highlight = i"
            @mousedown.prevent
            @keydown.enter.stop.prevent="toggleOption(opt.value)"
            @click.stop="toggleOption(opt.value)"
          >
            {{ opt.text }}
          </button>
        </template>

        <template v-else-if="draftFilter && draftFilter.options && draftOp !== OPERATORS.BETWEEN">
          <label v-for="opt in draftFilter.options" :key="opt.value" class="filter-bar__check">
            <input
              type="checkbox"
              :name="valueGroupName"
              :value="opt.value"
              :checked="isOptionChecked(opt.value)"
              @change="toggleOption(opt.value)"
            />
            <span>{{ opt.text }}</span>
          </label>
          <button type="button" class="filter-bar__apply" @mousedown.prevent @click.stop="commitDraft">Apply</button>
        </template>

        <template v-else-if="draftFilter && draftFilter.type === CONTROL_TYPES.DATE">
          <div v-if="draftOp === OPERATORS.BETWEEN" class="filter-bar__calendars">
            <div class="filter-bar__cal">
              <span class="filter-bar__cal-label">From</span>
              <input
                v-model="draftFrom"
                class="filter-bar__date-input"
                aria-label="From date"
                placeholder="YYYY-MM-DD"
                autocomplete="off"
                @keydown.enter.stop.prevent="commitDraft"
              />
              <b-calendar
                v-model="calendarFrom"
                :max="draftFilter.noFuture ? todayIso() : undefined"
                block
                hide-header
              />
            </div>
            <div class="filter-bar__cal">
              <span class="filter-bar__cal-label">To</span>
              <input
                v-model="draftTo"
                class="filter-bar__date-input"
                aria-label="To date"
                placeholder="YYYY-MM-DD"
                autocomplete="off"
                @keydown.enter.stop.prevent="commitDraft"
              />
              <b-calendar v-model="calendarTo" :max="draftFilter.noFuture ? todayIso() : undefined" block hide-header />
            </div>
          </div>
          <div v-else class="filter-bar__cal">
            <b-calendar
              v-model="calendarQuery"
              :max="draftFilter.noFuture ? todayIso() : undefined"
              block
              hide-header
            />
          </div>
          <button type="button" class="filter-bar__apply" @mousedown.prevent @click.stop="commitDraft">Apply</button>
        </template>

        <template v-else-if="draftOp === OPERATORS.BETWEEN">
          <div class="filter-bar__range">
            <label class="filter-bar__range-field">
              <span class="filter-bar__cal-label">From</span>
              <input
                v-model="draftFrom"
                type="number"
                class="filter-bar__date-input"
                placeholder="From"
                autocomplete="off"
                @keydown.enter.stop.prevent="commitDraft"
              />
            </label>
            <label class="filter-bar__range-field">
              <span class="filter-bar__cal-label">To</span>
              <input
                v-model="draftTo"
                type="number"
                class="filter-bar__date-input"
                placeholder="To"
                autocomplete="off"
                @keydown.enter.stop.prevent="commitDraft"
              />
            </label>
          </div>
          <button type="button" class="filter-bar__apply" @mousedown.prevent @click.stop="commitDraft">Apply</button>
        </template>

        <div v-else class="filter-bar__hint">{{ valueHint }}</div>

        <div v-if="error" class="filter-bar__error" role="alert">{{ error }}</div>
      </template>
    </div>
  </section>
</template>

<script setup>
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { CONTROL_TYPES } from '@/components/AppInput.vue';
import {
  FULL_DATE_RE,
  OPERATORS,
  OPERATOR_LABELS,
  VALUE_FORMAT_HINTS,
  availableOperators,
  dateRangeError,
  filterTypeForControl,
  formatFilterValue,
  initialValueForOperator,
  isCommittableFilterValue,
  isEmptyValue,
  isMultiValueOperator,
  matchOperatorsByLabel,
  matchOptionsByLabel,
  nextHighlightIndex,
  operatorFor,
  operatorSymbolFor,
  operatorUsesMenu,
  parseFiltersFromQuery,
  reconcileDateRange,
  serializeFilters,
  suggestFilterColumns,
  todayIso,
} from '@/components/filterBar.helpers';

const props = defineProps({
  filters: {
    type: Array,
    required: true,
  },
  value: {
    type: Object,
    required: true,
  },
  operators: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    default: 'Filter…',
  },
  // Free-text search (name or email), distinct from the per-column filters.
  search: {
    type: String,
    default: '',
  },
  // Off when the parent pins the search term, so the bar doesn't offer a dead-end search row.
  searchable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['input', 'update:operators', 'update:search']);

const $route = useRoute();
const $router = useRouter();

const $root = ref(undefined);
const $input = ref(undefined);
const $menu = ref(undefined);

// Ids must be per-instance: two bars on one page (e.g. sibling Employee tabs) would otherwise collide.
const uid = `filter-bar-${getCurrentInstance().uid}`;
const menuId = `${uid}-menu`;
const valueGroupName = `${uid}-value`;
const optionId = index => `${uid}-opt-${index}`;

const query = ref('');
const isOpen = ref(false);
const stage = ref('column');
const highlight = ref(-1);
const error = ref('');

const draftKey = ref(null);
const draftOp = ref(null);
const draftVal = ref(null);

const getFilter = key => props.filters.find(filter => filter.key === key);

const tokens = computed(() =>
  Object.entries(props.value)
    .filter(([key, value]) => !isEmptyValue(value) && filterKeys.value.includes(key))
    .map(([key, value]) => ({ key, op: props.operators[key] || operatorFor(getFilter(key)), value })),
);

const committedKeys = computed(() => new Set(tokens.value.map(token => token.key)));

// The token being edited is hidden, not removed — abandoning the edit leaves the original filter intact.
const visibleTokens = computed(() => tokens.value.filter(token => token.key !== draftKey.value));

const columnSuggestions = computed(() =>
  suggestFilterColumns(props.filters, {
    query: query.value,
    committedKeys: committedKeys.value,
    draftKey: draftKey.value,
  }),
);

const draftFilter = computed(() => getFilter(draftKey.value));
const draftOperators = computed(() => (draftFilter.value ? availableOperators(draftFilter.value) : []));
const draftMulti = computed(() => isMultiValueOperator(draftOp.value));
const draftFilterType = computed(() => filterTypeForControl(draftFilter.value?.type));

// Single-choice option columns are a typeahead like the earlier stages. Multi-choice keeps checkboxes, which have to
// show several states at once. The value still comes from the picked option, never the typed text (values can be UUIDs).
const optionTypeahead = computed(
  () => !!draftFilter.value?.options?.length && !draftMulti.value && draftOp.value !== OPERATORS.BETWEEN,
);
const valueStageOptions = computed(() =>
  optionTypeahead.value ? matchOptionsByLabel(draftFilter.value.options, query.value) : [],
);

// Operator stage is also typeahead: narrow by what's typed (e.g. "eq" -> Equals / Not equal).
const visibleOperators = computed(() => matchOperatorsByLabel(draftOperators.value, query.value));

// Calendars must only ever READ a complete date — bound straight to the text, they re-parse partial
// input mid-typing (e.g. "2026-06-0") and overwrite it with a normalised date.
const calendarBound = textRef =>
  computed({
    get: () => (FULL_DATE_RE.test(textRef.value || '') ? textRef.value : ''),
    // b-calendar echoes the getter's '' straight back as an input event, which would wipe the rest of a
    // half-typed date; a click always yields a real date, so dropping the empty write drops only the echo.
    set: date => {
      if (date) {
        textRef.value = date;
      }
    },
  });

// Setting one bound drags the other when the pair would breach the cap; non-date ranges pass straight through.
const setRangeBound = (edited, value) => {
  // The watch below only clears on query/stage; editing a bound changes neither, so a fixed range kept its error.
  error.value = '';
  const base = typeof draftVal.value === 'object' && draftVal.value ? draftVal.value : {};
  const next = { ...base, [edited]: value };
  draftVal.value =
    draftFilter.value?.type === CONTROL_TYPES.DATE ? reconcileDateRange(next, edited, draftFilter.value) : next;
};

const draftFrom = computed({
  get: () => draftVal.value?.from ?? '',
  set: from => setRangeBound('from', from),
});
const draftTo = computed({
  get: () => draftVal.value?.to ?? '',
  set: to => setRangeBound('to', to),
});

const calendarQuery = calendarBound(query);
const calendarFrom = calendarBound(draftFrom);
const calendarTo = calendarBound(draftTo);

// Pinned columns express a scope the backend always applies, so they can be edited but never removed.
const isPinned = key => !!getFilter(key)?.pinned;
const removableKeys = computed(() => filterKeys.value.filter(key => !isPinned(key)));
const canClearAll = computed(
  () => tokens.value.some(token => !isPinned(token.key)) || !!draftKey.value || !!query.value || !!props.search,
);

// `between` and categorical use the menu (draftVal); everything else is typed in the top-bar input (query).
const draftUsesMenu = () => operatorUsesMenu(draftOp.value, draftFilter.value);

const focusInput = () => {
  isOpen.value = true;
  nextTick(() => $input.value?.focus());
};

const resetDraft = () => {
  draftKey.value = null;
  draftOp.value = null;
  draftVal.value = null;
  stage.value = 'column';
  query.value = '';
  highlight.value = -1;
  error.value = '';
};

const enterValueStage = () => {
  stage.value = 'value';
  highlight.value = -1;
  draftVal.value = initialValueForOperator(draftOp.value);
  query.value = '';
  // An option typeahead is driven from the bar input; only real form controls take focus inside the menu.
  nextTick(() =>
    draftUsesMenu() && !optionTypeahead.value ? $menu.value?.querySelector('input')?.focus() : $input.value?.focus(),
  );
};

const pickColumn = col => {
  draftKey.value = col.key;
  query.value = '';
  highlight.value = -1;
  const ops = availableOperators(col);
  if (ops.length <= 1) {
    draftOp.value = ops[0] || OPERATORS.EQ;
    enterValueStage();
  } else {
    stage.value = 'operator';
    nextTick(() => $input.value?.focus());
  }
};

const pickOperator = op => {
  draftOp.value = op;
  enterValueStage();
};

const commitDraft = () => {
  const key = draftKey.value;
  if (!key) {
    return;
  }
  const filterType = draftFilterType.value;
  const val = draftUsesMenu() ? draftVal.value : query.value.trim();
  // Emptying an already-applied filter removes it; its chip is hidden while editing, so there's no other way out.
  if (isEmptyValue(val)) {
    if (isPinned(key)) {
      error.value = `${draftFilter.value?.label} can't be emptied`;
      return;
    }
    if (committedKeys.value.has(key)) {
      removeToken(key);
      resetDraft();
      nextTick(() => $input.value?.focus());
    }
    return;
  }
  if (!isCommittableFilterValue(filterType, draftOp.value, val)) {
    // Silently refusing to commit reads as a broken bar, so say what's wrong.
    error.value = VALUE_FORMAT_HINTS[filterType] || 'Enter a valid value';
    return;
  }
  // Typing sidesteps the calendars' min/max, so the window is re-checked here rather than left to 400.
  const rangeError = dateRangeError(draftFilter.value, val);
  if (rangeError) {
    error.value = rangeError;
    return;
  }
  emit('input', { ...props.value, [key]: val });
  emit('update:operators', { ...props.operators, [key]: draftOp.value });
  resetDraft();
  nextTick(() => $input.value?.focus());
};

const removeToken = key => {
  if (isPinned(key)) {
    return;
  }
  emit('input', omit(props.value, key));
  emit('update:operators', omit(props.operators, key));
};

const editToken = token => {
  draftKey.value = token.key;
  draftOp.value = token.op;
  draftVal.value = token.value;
  stage.value = 'value';
  isOpen.value = true;
  highlight.value = -1;
  query.value = draftUsesMenu() ? '' : String(token.value);
  nextTick(() => $input.value?.focus());
};

const clearAll = () => {
  emit('input', omit(props.value, removableKeys.value));
  emit('update:operators', omit(props.operators, removableKeys.value));
  clearSearch();
  resetDraft();
};

const toggleOption = optionValue => {
  if (draftMulti.value) {
    const arr = Array.isArray(draftVal.value) ? draftVal.value : [];
    draftVal.value = arr.includes(optionValue) ? arr.filter(v => v !== optionValue) : [...arr, optionValue];
  } else {
    draftVal.value = optionValue;
    commitDraft();
  }
};

const isOptionChecked = optionValue =>
  draftMulti.value
    ? Array.isArray(draftVal.value) && draftVal.value.includes(optionValue)
    : draftVal.value === optionValue;

// The column stage also offers a trailing "Search for …" row, so it participates in arrow nav / Enter.
const SEARCH_ITEM = { __search: true };
const columnStageItems = computed(() =>
  props.searchable && query.value.trim() ? [...columnSuggestions.value, SEARCH_ITEM] : columnSuggestions.value,
);

const commitSearch = () => {
  const text = query.value.trim();
  if (!text) {
    return;
  }
  emit('update:search', text);
  resetDraft();
  focusInput();
};

const clearSearch = () => emit('update:search', '');

const currentList = () => {
  if (stage.value === 'column') {
    return columnStageItems.value;
  }
  if (stage.value === 'operator') {
    return visibleOperators.value;
  }
  // Empty for every other value control, so the arrow keys stay inert where there is no list.
  return valueStageOptions.value;
};

const scrollActiveIntoView = () => {
  nextTick(() => {
    // Queried by ARIA state, not the style class, so renaming the class can't silently break scrolling.
    // Scoped to option rows: b-calendar marks its selected day aria-selected too, and scrolling that is wrong.
    const el = $menu.value?.querySelector('[role="option"][aria-selected="true"]');
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  });
};

const moveHighlight = dir => {
  const list = currentList();
  if (!list.length) {
    return;
  }
  highlight.value = nextHighlightIndex(highlight.value, dir, list.length);
  scrollActiveIntoView();
};

// The row Enter/Tab would take: an explicit highlight, else the top one once something has been typed.
const highlightedItem = () => {
  const list = currentList();
  if (highlight.value < 0 && !query.value.trim()) {
    return null;
  }
  return list[highlight.value] || list[0] || null;
};

// Column and operator stages advance a stage; an option commits the filter; the search row ends the interaction.
const acceptHighlighted = item => {
  if (stage.value === 'value') {
    toggleOption(item.value);
    return;
  }
  if (stage.value === 'column') {
    if (item.__search) {
      commitSearch();
    } else {
      pickColumn(item);
    }
    return;
  }
  pickOperator(item);
};

const onEnter = () => {
  const item = highlightedItem();
  if (item) {
    acceptHighlighted(item);
    return;
  }
  if (stage.value === 'value') {
    commitDraft();
  }
};

// Tab completes like a shell, but only when there is a row to take — otherwise it must stay native, or a keyboard
// user can't leave the bar. Native at the value stage too, so focus can still reach the calendars and Apply.
const onTab = event => {
  if (stage.value === 'value' && !optionTypeahead.value) {
    return;
  }
  const item = highlightedItem();
  if (!item) {
    return;
  }
  event.preventDefault();
  acceptHighlighted(item);
};

// Backspace walks back through the chips; a pinned one is skipped so it can't become a dead end.
const lastRemovableToken = () => [...tokens.value].reverse().find(token => !isPinned(token.key));

const onBackspace = event => {
  if (query.value) {
    return;
  }
  if (stage.value === 'value') {
    event.preventDefault();
    if (draftOperators.value.length > 1) {
      stage.value = 'operator';
      draftOp.value = null;
      draftVal.value = null;
    } else {
      resetDraft();
    }
    focusInput();
  } else if (stage.value === 'operator') {
    event.preventDefault();
    draftKey.value = null;
    stage.value = 'column';
    focusInput();
  } else if (lastRemovableToken()) {
    event.preventDefault();
    removeToken(lastRemovableToken().key);
    focusInput();
  } else if (props.search) {
    event.preventDefault();
    clearSearch();
  }
};

const cancelDraft = () => {
  resetDraft();
  isOpen.value = false;
};

// Auto-highlight the top match while typing, so Enter is obvious. Gated on the list actually having rows,
// so a value stage that renders no list (text, number, date) never highlights nothing.
watch([query, stage], () => {
  error.value = '';
  highlight.value = query.value.trim() && currentList().length ? 0 : -1;
  // Long option lists scroll, so re-highlighting row 0 has to bring it back into view.
  scrollActiveIntoView();
});

// The value stage fills the menu with form controls, which is not a listbox — unless it's an option typeahead.
const menuRole = computed(() => (stage.value === 'value' && !optionTypeahead.value ? 'group' : 'listbox'));

const valueHint = computed(() => {
  const format = VALUE_FORMAT_HINTS[draftFilterType.value];
  return format ? `${format}, then press Enter` : 'Type a value, then press Enter';
});

const symbolFor = token => operatorSymbolFor(token.op, token.value);

const displayValue = token => formatFilterValue(getFilter(token.key), token.value);

// URL sync: ?field[op]=value; a bare field means eq.
const filterKeys = computed(() => props.filters.map(filter => filter.key));
const baseQueryKey = key => key.replace(/\[\w+\]$/, '');

// Only claim URL keys this bar can actually surface — a hidden column's key may belong to
// another component on the page (pay_schedule_id is also the streamed-wages filter).
const ownedQueryKeys = computed(
  () =>
    new Set([
      ...props.filters.filter(filter => filter.hidden !== true).map(filter => filter.key),
      ...committedKeys.value,
    ]),
);

const SEARCH_QUERY_KEY = 'searchText';

const writeQuery = () => {
  const current = $route.query;
  // searchText is ours too, so a cleared search actually leaves the URL.
  const external = pickBy(
    current,
    (value, key) => key !== SEARCH_QUERY_KEY && !ownedQueryKeys.value.has(baseQueryKey(key)),
  );
  const structured = {};
  tokens.value.forEach(token => {
    structured[token.key] = { op: token.op, value: token.value };
  });
  const next = {
    ...serializeFilters(structured),
    ...(props.search ? { [SEARCH_QUERY_KEY]: props.search } : null),
    ...external,
  };
  // Compare loosely: a single-value array and a bare string are the same URL to vue-router.
  const comparable = query => Object.fromEntries(Object.entries(query).map(([k, v]) => [k, [].concat(v).map(String)]));
  if (!isEqual(comparable(current), comparable(next))) {
    $router.replace({ query: next });
  }
};

// Unmounting inside the mount tick would leave the async watcher below with no handle to stop it.
let stopWriteQuery;
let unmounted = false;

const onDocumentMousedown = event => {
  if ($root.value && !$root.value.contains(event.target)) {
    // Drop the draft too: an abandoned edit otherwise leaves its chip hidden while the filter stays applied.
    resetDraft();
    isOpen.value = false;
  }
};

onMounted(() => {
  const parsed = parseFiltersFromQuery($route.query);
  const nextValues = {};
  const nextOperators = {};
  props.filters.forEach(filter => {
    const entry = parsed[filter.key];
    if (entry && !isEmptyValue(entry.value)) {
      const offered = availableOperators(filter);
      const op = offered.includes(entry.op) ? entry.op : operatorFor(filter);
      const isRange = entry.value && typeof entry.value === 'object' && !Array.isArray(entry.value);
      // A coerced operator has to match the value's shape, both ways, or the wrong control renders.
      if (isRange !== (op === OPERATORS.BETWEEN) || (Array.isArray(entry.value) && !isMultiValueOperator(op))) {
        return;
      }
      // A stale or hand-edited link can carry a window that is too wide, or a date that isn't one at all.
      // Shape first: a malformed date makes the range maths NaN, and NaN > max is false, so width alone lets it through.
      const filterType = filterTypeForControl(filter.type);
      if (!isCommittableFilterValue(filterType, op, entry.value) || dateRangeError(filter, entry.value)) {
        return;
      }
      // A v1 URL writes ?source=UPLOAD, which coerces to `in` — the scalar has to become a list.
      nextValues[filter.key] = isMultiValueOperator(op) ? [].concat(entry.value) : entry.value;
      nextOperators[filter.key] = op;
    }
  });
  if (Object.keys(nextValues).length) {
    emit('input', { ...props.value, ...nextValues });
    emit('update:operators', { ...props.operators, ...nextOperators });
  }
  const querySearch = $route.query[SEARCH_QUERY_KEY];
  if (querySearch && !props.search) {
    emit('update:search', String(querySearch));
  }
  // Registered async, so it isn't owned by the instance scope — keep the handle and stop it ourselves.
  nextTick(() => {
    if (unmounted) {
      return;
    }
    stopWriteQuery = watch(() => [props.value, props.operators, props.search], writeQuery, { deep: true });
  });
  document.addEventListener('mousedown', onDocumentMousedown);
});

onBeforeUnmount(() => {
  unmounted = true;
  stopWriteQuery?.();
  document.removeEventListener('mousedown', onDocumentMousedown);
});
</script>

<style lang="scss" scoped>
.filter-bar {
  align-items: center;
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-95);
  border-radius: 0.5rem;
  cursor: text;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 24rem;
  padding: 0.6rem 0.75rem;
  position: relative;
}

.filter-bar--focused {
  border-color: var(--palette-color-brand-primary);
}

.filter-bar__icon {
  color: var(--palette-color-default-lighten-40);
  flex-shrink: 0;
}

.filter-bar__token,
.filter-bar__draft {
  align-items: center;
  background: var(--palette-color-default-lighten-95);
  border: 0;
  border-radius: 0.25rem;
  display: inline-flex;
  gap: 0.375rem;
  padding: 0.125rem 0.5rem;
}

.filter-bar__token {
  max-width: 20rem;
  min-width: 0;
}

// A real button, so the chip is editable by keyboard and its remove sibling stays separately focusable.
.filter-bar__token-edit {
  align-items: center;
  background: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  gap: 0.375rem;
  min-width: 0;
  padding: 0;
}

.filter-bar__token-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-bar__token-label {
  font-weight: 600;
}

.filter-bar__token-op {
  color: var(--palette-color-brand-primary);
  font-weight: 600;
}

.filter-bar__token-remove {
  background: none;
  border: 0;
  cursor: pointer;
  line-height: 1;
  margin-left: 0.125rem;
  padding: 0;
}

.filter-bar__input {
  background: transparent;
  border: 0;
  flex: 1 1 6rem;
  min-width: 6rem;
  outline: none;
}

.filter-bar__clear {
  background: none;
  border: 0;
  color: var(--palette-color-default-lighten-40);
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  padding: 0;
}

.filter-bar__menu {
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-95);
  border-radius: 0.5rem;
  box-shadow: var(--depth-2);
  left: 0;
  max-height: 32rem;
  overflow: auto;
  padding: 0.25rem;
  position: absolute;
  top: calc(100% + 0.25rem);
  z-index: 1000;
}

.filter-bar__option {
  background: none;
  border: 0;
  border-radius: 0.25rem;
  cursor: pointer;
  display: block;
  padding: 0.5rem 0.75rem;
  text-align: left;
  width: 100%;
}

.filter-bar__option--active {
  background-color: var(--palette-color-default-lighten-95);
}

// Which option is already applied — the radio it replaced showed this, so the typeahead has to as well.
.filter-bar__option--selected {
  font-weight: 600;

  &::after {
    content: '✓';
    float: right;
  }
}

.filter-bar__option-hint {
  color: var(--palette-color-default-lighten-40);
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.filter-bar__empty,
.filter-bar__hint {
  color: var(--palette-color-default-lighten-40);
  padding: 0.5rem 0.75rem;
}

.filter-bar__error {
  color: var(--palette-color-danger);
  padding: 0 0.75rem 0.5rem;
}

.filter-bar__check {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin: 0;
  padding: 0.375rem 0.75rem;
}

.filter-bar__apply {
  background: var(--palette-color-brand-primary);
  border: 0;
  border-radius: 0.25rem;
  color: var(--palette-color-base-white);
  cursor: pointer;
  margin: 0.25rem;
  padding: 0.375rem 0.75rem;
}

.filter-bar__calendars {
  display: flex;
  gap: 0.75rem;
  padding: 0.5rem;
}

.filter-bar__cal {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 14rem;
}

.filter-bar__date-input {
  appearance: textfield;
  border: 1px solid var(--palette-color-default-lighten-95);
  border-radius: 0.25rem;
  outline: none;
  padding: 0.375rem 0.5rem;
  width: 100%;
}

.filter-bar__date-input::-webkit-inner-spin-button,
.filter-bar__date-input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.filter-bar__range {
  display: flex;
  gap: 0.75rem;
  padding: 0.5rem;
}

.filter-bar__range-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: 0;
}

.filter-bar__range-field .filter-bar__date-input {
  width: 9rem;
}

.filter-bar__cal-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

// Two calendars side by side don't fit a phone, and the bar must not exceed its column.
@media (max-width: 576px) {
  .filter-bar {
    min-width: 100%;
  }

  .filter-bar__menu {
    max-width: calc(100vw - 2rem);
  }

  .filter-bar__calendars {
    flex-direction: column;
  }
}
</style>
